import {
  PreviewLinkCard,
  PreviewLinkCardContent,
  PreviewLinkCardImage,
  PreviewLinkCardTrigger,
} from "@do/ui/components/preview-link-card";
import { cn } from "@do/ui/lib/utils";
import { useTranslations } from "next-intl";
import { memo } from "react";
import Markdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useOptionalChat } from "../providers/chat-provider";

const getEffectiveAlt = (alt: string | undefined, t: (key: string) => string) => {
  const normalizedAlt = alt?.trim();
  if (normalizedAlt) return normalizedAlt;
  return t("chat.markdown.accessibility.image-default-alt");
};

export type MarkdownRendererProps = {
  allowFullScreenImg?: boolean;
  text: string;
  onImageLoad?: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

export const MarkdownRenderer = memo<MarkdownRendererProps>(
  ({ allowFullScreenImg, text, onImageLoad, className, ...props }) => {
    const t = useTranslations("do-chat-v2");
    const chat = useOptionalChat();
    const rewriteUrl = chat?.rewriteUrl;
    const finalText = text
      ? text
          .replace(/```[\s\S]*?```/g, (m) => m.replace(/\n/g, "\n "))
          .replace(/(?<=\n\n)(?![*-])\n/g, "&nbsp;\n ")
      : text;

    const img = ({
      alt,
      src,
      ...imgProps
    }: {
      alt?: string;
      src?: string;
      [key: string]: unknown;
    }) => {
      const srcStr = typeof src === "string" ? src : "";
      const effectiveAlt = getEffectiveAlt(alt, t);

      if (allowFullScreenImg) {
        return (
          <a
            href={srcStr}
            target="_blank"
            rel="noreferrer"
            aria-label={t("chat.markdown.accessibility.open-image-in-new-tab-aria-label", {
              alt: effectiveAlt,
            })}
          >
            <img alt={effectiveAlt} {...imgProps} src={srcStr} onLoad={onImageLoad} />
          </a>
        );
      }

      return <img alt={effectiveAlt} {...imgProps} src={srcStr} onLoad={onImageLoad} />;
    };

    const components = {
      img,
      a({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
        const finalHref = rewriteUrl && href ? rewriteUrl(href) : href;
        return (
          <PreviewLinkCard href={finalHref ?? ""}>
            <PreviewLinkCardTrigger asChild>
              <a href={finalHref} target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            </PreviewLinkCardTrigger>

            <PreviewLinkCardContent target="_blank" rel="noreferrer">
              <PreviewLinkCardImage />
            </PreviewLinkCardContent>
          </PreviewLinkCard>
        );
      },
    };

    return (
      <div className={cn("markdown-renderer max-w-none", className)} {...props}>
        <Markdown
          components={components as Components}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeRaw]}
        >
          {finalText}
        </Markdown>
      </div>
    );
  },
  (oldProps, newProps) =>
    oldProps.text === newProps.text && oldProps.onImageLoad === newProps.onImageLoad
);

MarkdownRenderer.displayName = "MarkdownRenderer";
